import { useCallback, useEffect, useRef, useState } from 'react';

interface UseTimerReturn {
    timeLeft: number;
    resetTimer: () => void;
    isExpired: boolean;
    isActive: boolean;
    cancelTimer: () => void;
    formatTime: (seconds: number) => string;
}

export const useCountDownTimer = (initialTime: number = 60): UseTimerReturn => {
    const [timeLeft, setTimeLeft] = useState(initialTime);
    const [isActive, setIsActive] = useState(true);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const isExpired = timeLeft <= 0;

    useEffect(() => {
        if (!isActive) return;
        intervalRef.current = setInterval(() => {
            setTimeLeft(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isActive]);

    const resetTimer = useCallback(() => {
        setTimeLeft(initialTime);
        setIsActive(true);
    }, [initialTime]);

    const cancelTimer = useCallback(() => {
        setIsActive(false);
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const formatTime = useCallback((seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }, []);

    return { timeLeft, resetTimer, cancelTimer, formatTime, isExpired, isActive };
};
